// src/pages/auth/VerifyEmail.jsx
//
// Post-signup landing screen. The user just registered; we sent a
// verification email via the sendVerificationEmail Cloud Function
// (custom SendGrid template, not the default Firebase one — see
// ForgotPassword.jsx for why). This page polls Firebase every 3s,
// auto-advances when emailVerified flips true, and offers a resend
// button with a 30s cooldown.
import { useEffect, useMemo, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '@/context/AuthContext';
import { db } from '@/firebase/firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import AuthShell, {
  AuthPill,
  AuthHeading,
  AuthSubtitle,
  AuthButton,
  AuthButtonSecondary,
} from '@/components/auth/AuthShell';

export default function VerifyEmail() {
  const {
    loadingAuth,
    profileLoading,
    refreshProfile,
    grantTrustBadge,
  } = useAuth();

  const loading = useMemo(
    () => loadingAuth || profileLoading,
    [loadingAuth, profileLoading]
  );

  const [user, setUser] = useState(null);
  const [cooldown, setCooldown] = useState(false);
  const [sending, setSending] = useState(false);

  const navigate = useNavigate();
  const auth = getAuth();
  const functions = getFunctions();

  // Independent listener (not via global context) to dodge race
  // conditions between the post-signup AuthContext refresh and this
  // page mounting. We just want the latest user object for polling.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, [auth]);

  const syncEmailVerifiedToFirestore = async (uid) => {
    if (!uid) return;
    try {
      await setDoc(
        doc(db, 'users', uid),
        {
          emailVerified: true,
          verificationStatus: 'verified_email',
          updatedAt: serverTimestamp(),
          trust: {
            emailVerified: true,
            badges: { emailVerified: true },
          },
        },
        { merge: true }
      );
      if (typeof grantTrustBadge === 'function') {
        await grantTrustBadge('email_verified');
      }
      if (typeof refreshProfile === 'function') {
        await refreshProfile();
      }
    } catch (err) {
      console.error('[VerifyEmail] failed to sync emailVerified to Firestore', err);
      toast.error('Could not update verification status in your profile.');
    }
  };

  // Polling loop — checks every 3 seconds whether the user has
  // clicked the verification link in their inbox. As soon as we see
  // emailVerified flip true we sync to Firestore + redirect.
  useEffect(() => {
    let intervalId = null;
    if (loading && !user) return;

    intervalId = setInterval(async () => {
      const current = auth.currentUser;
      if (!current) return;
      try {
        await current.reload();
        if (current.emailVerified) {
          await syncEmailVerifiedToFirestore(current.uid);
          toast.success('Email verified! Redirecting…');
          clearInterval(intervalId);
          intervalId = null;
          navigate('/dashboard', { replace: true });
        }
      } catch {
        // Silent — we'll try again next tick
      }
    }, 3000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [loading, navigate, auth, user]);

  const handleResend = async () => {
    if (cooldown || sending) return;

    const current = auth.currentUser;
    if (!current) {
      toast.error('You must be signed in to resend verification.');
      return;
    }

    setSending(true);
    setCooldown(true);

    try {
      await current.reload();
      if (!current.emailVerified) {
        const sendCustomVerification = httpsCallable(functions, 'sendVerificationEmail');
        await sendCustomVerification();
        toast.success('Verification email resent!');
      } else {
        await syncEmailVerifiedToFirestore(current.uid);
        toast.success("You're already verified.");
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.error('[VerifyEmail] resend error', err);
      toast.error('Failed to resend verification email.');
    } finally {
      setSending(false);
      setTimeout(() => setCooldown(false), 30000);
    }
  };

  const emailDisplay = user?.email || 'your email';

  return (
    <AuthShell cardWidth="narrow">
      <div className="text-center">
        <AuthPill>Verify your email</AuthPill>

        {/* Envelope icon */}
        <div className="mx-auto mt-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/30">
          <svg
            className="h-7 w-7 text-[#b5f03d]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>

        <AuthHeading size="small">
          Check your <span className="text-[#b5f03d]">inbox.</span>
        </AuthHeading>

        <AuthSubtitle>
          A verification link is on its way to <strong className="text-white">{emailDisplay}</strong>.
          This page will continue automatically the moment you click it.
        </AuthSubtitle>

        <p className="mt-3 text-xs text-white/40">
          Don't see it? Check your spam or promotions folder.
        </p>
      </div>

      <div className="mt-7 space-y-3">
        <AuthButton
          type="button"
          onClick={handleResend}
          disabled={sending || cooldown}
          busy={sending}
        >
          {sending ? 'On it…' : cooldown ? 'Sent — wait 30 seconds' : 'Resend verification email'}
        </AuthButton>

        <AuthButtonSecondary
          type="button"
          onClick={() => navigate('/login')}
        >
          Use a different email
        </AuthButtonSecondary>
      </div>
    </AuthShell>
  );
}
